import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { formatISO } from "date-fns";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { ReportFormat } from "@common/helpers/reports";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { CheckDetailGroupBy } from "@common/types/reports/check-detail";
import { CheckDetailColumn } from "@common/types/views/checkDetailView";
import { accountingReportsContract } from "@common/contracts/accounting-reports.contract";
import {
    type ChartOfAccountsEntityGroup,
    getAccountTransactionsResponseWithPresentationSchema,
} from "@common/schemas/domains/accounting/report.schema";
import {
    eliminationDetailReportResponseSchema,
    eliminationExceptionsReportResponseSchema,
} from "@common/schemas/domains/accounting/elimination.schema";
import { EliminationExceptionReason } from "@common/types/domains/accounting/elimination";
import { ExpenseByVendorSort } from "@common/helpers/expenseByVendorSort";
import { compareAccountCodes } from "@common/helpers/accounting";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { AccountingReportFilters } from "@components/accounting/types";
import {
    mapArAgingFilters,
    mapGeneralLedgerFilters,
} from "@components/accounting/accountingReportFilters.lib";
import { ExtendedAccount } from "@common/types/reports/types";
import { useEffect, useState } from "react";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { AccountingReportCycleTotal } from "@common/constants";
import { AccountingReportType } from "@common/types/reports/report-type";
import { ReportPackageReportSetting } from "@common/types/reports/report-package";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";

const accountingReportsContractClient = getBackendAPIClient(
    accountingReportsContract,
);

const accountingReportsExportContractClient = getBackendAPIClient(
    accountingReportsContract,
    "blob",
);

export const accountingReportsQueryKeys = {
    chartOfAccounts: "chart-of-accounts",
    trialBalance: "trial-balance",
    balanceSheet: "balance-sheet",
    profitAndLoss: "profit-loss",
    accountTransactions: "account-transactions",
    expenseByVendor: "expenses-by-vendor",
    cashFlowStatement: "cash-flow-statement",
    bankReconciliationReport: "bank-reconciliation-report",
    eliminationDetailReport: "elimination-detail-report",
    eliminationExceptionsReport: "elimination-exceptions-report",
    arAgingSummary: "ar-aging-summary",
    arAgingDetail: "ar-aging-detail",
    checkDetail: "check-detail",
};

export function getChartOfAccountsQueryKey(
    availableClassifications?: AccountClass[],
) {
    return [
        accountingReportsQueryKeys.chartOfAccounts,
        availableClassifications,
    ];
}

function getTrialBalanceQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.trialBalance, query];
}

function getBalanceSheetQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.balanceSheet, query];
}

function getProfitAndLossQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.profitAndLoss, query];
}

function getExpenseByVendorQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.expenseByVendor, query];
}

export function getAccountTransactionsQueryKey(
    query: AccountTransactionsQuery,
) {
    return [
        accountingReportsQueryKeys.accountTransactions,
        prepareAccountTransactionsQuery(query),
    ];
}

function getEliminationDetailReportQueryKey(
    query: EliminationDetailReportQuery | undefined,
) {
    return [accountingReportsQueryKeys.eliminationDetailReport, query];
}

function getEliminationExceptionsReportQueryKey(
    query: EliminationExceptionsReportQuery | undefined,
) {
    return [accountingReportsQueryKeys.eliminationExceptionsReport, query];
}

function getArAgingSummaryQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.arAgingSummary, query];
}

function getArAgingDetailQueryKey(
    query: AccountingReportFilters,
    buckets: ArAgingBucket[] | undefined,
) {
    return [accountingReportsQueryKeys.arAgingDetail, query, buckets];
}

// AR Aging's query shape diverges from every other accounting report: it
// takes a single `asOfDate` (not a start/end range) and a required
// `entityIds` array (not a single `entityId` + `ledgerId`), so it needs its
// own prep function rather than reusing `prepareAccountingReportQuery`.
function prepareArAgingReportQuery(filters: AccountingReportFilters) {
    const { entityIds } = filters;
    if (!entityIds || entityIds.length === 0) {
        return undefined;
    }

    return {
        asOfDate: filters.asOfDate
            ? formatISO(filters.asOfDate, { representation: "date" })
            : undefined,
        entityIds,
        ...mapArAgingFilters(filters),
    };
}

interface CheckDetailQueryParams {
    filters: AccountingReportFilters;
    entityIds: number[];
}

function prepareCheckDetailReportQuery({
    filters,
    entityIds,
}: CheckDetailQueryParams) {
    const { startDate, endDate } = filters;
    if (!startDate || !endDate || entityIds.length === 0) {
        return undefined;
    }

    return {
        entityIds,
        startDate: formatISO(startDate, { representation: "date" }),
        endDate: formatISO(endDate, { representation: "date" }),
        financialAccountIds: filters.accountIds,
        search: filters.search,
        counterpartiesIds: filters.counterpartiesIds,
        withEmptyOrUnverifiedCounterparty:
            filters.withEmptyOrUnverifiedCounterparty,
        hasCounterparty: filters.hasCounterparty,
        minAmount: filters.minAmount,
        maxAmount: filters.maxAmount,
        amountRounded: filters.amountRounded,
        amountRoundingLevel: filters.amountRoundingLevel,
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        excludeFrom1099: filters.excludeFrom1099,
    };
}

export interface ChartOfAccountsQueryData {
    accounts: ExtendedAccount[];
    groups: ChartOfAccountsEntityGroup[];
}

function compareBySortOrderThenCode(
    a: ExtendedAccount,
    b: ExtendedAccount,
): number {
    if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
    }
    return compareAccountCodes(a.codeText, b.codeText);
}

export function sortChartOfAccountsData(
    data: ChartOfAccountsQueryData,
): ChartOfAccountsQueryData {
    return {
        ...data,
        accounts: data.accounts.toSorted(compareBySortOrderThenCode),
    };
}

export function useChartOfAccountsQuery(
    availableClassifications?: AccountClass[],
) {
    const workspace = useActiveWorkspace();
    return useQuery<ChartOfAccountsQueryData>({
        queryKey: getChartOfAccountsQueryKey(availableClassifications),
        queryFn: async () => {
            if (!workspace) {
                return { accounts: [], groups: [] };
            }

            const response =
                await accountingReportsContractClient.getChartOfAccounts({
                    params: {
                        workspaceId: workspace.id,
                    },
                    query: {
                        availableClassifications,
                    },
                });
            return response.body;
        },
        select: sortChartOfAccountsData,
        enabled: !!workspace,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
    });
}

// Fetches a CoA for an explicit (non-active) workspace so an org admin can
// preview a source entity's CoA from a client workspace they administer — access
// resolves via the caller's workspace assignment.
export function useChartOfAccountsQueryForWorkspace(
    workspaceId: string | null,
    options?: { enabled?: boolean },
) {
    const isEnabled = (options?.enabled ?? true) && !!workspaceId;
    return useQuery<ChartOfAccountsQueryData>({
        queryKey: [
            accountingReportsQueryKeys.chartOfAccounts,
            "workspace",
            workspaceId,
        ],
        queryFn: async () => {
            if (!workspaceId) {
                return { accounts: [], groups: [] };
            }

            const response =
                await accountingReportsContractClient.getChartOfAccounts({
                    params: { workspaceId },
                    query: {},
                });
            return response.body;
        },
        select: sortChartOfAccountsData,
        enabled: isEnabled,
        refetchOnWindowFocus: false,
    });
}

const prepareAccountingReportQuery = (query: AccountingReportFilters) => {
    const { entityId, startDate, endDate, ledgerId } = query;
    if (!startDate || !endDate || !entityId || !ledgerId) {
        return undefined;
    }

    const hasComparisons = (query.comparisons?.length ?? 0) > 0;

    return {
        ...query,
        startDate: formatISO(startDate, { representation: "date" }),
        endDate: formatISO(endDate, { representation: "date" }),
        cycle:
            query.cycle === AccountingReportCycleTotal.TOTAL
                ? undefined
                : query.cycle,
        entityId,
        comparisons: query.comparisons,
        comparisonDiffs: hasComparisons ? query.comparisonDiffs : undefined,
        ratioTypes: query.ratioTypes,
        ledgerId,
        // Consolidated reports need both: consolidationGroupId selects the group
        // and workspaceId lets the (entity-scoped) request resolve the workspace
        // so the backend gates the consolidated layout on.
        consolidationGroupId: query.consolidationGroupId,
        workspaceId: query.consolidationGroupId ? query.workspaceId : undefined,
        financialAccountIds: query.accountIds,
        subaccountIds: query.subaccountIds,
    };
};

export function useExpenseByVendorQuery(query: AccountingReportFilters) {
    const result = useQuery({
        queryKey: getExpenseByVendorQueryKey(query),
        queryFn: async () => {
            const preparedQuery = prepareAccountingReportQuery(query);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getExpenseByVendor({
                    query: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });
    const generationDate = useGenerationDate(result.data);
    return { expenseByVendor: result, generationDate };
}

export async function exportExpenseByVendor(
    params: AccountingReportFilters,
    sort?: ExpenseByVendorSort,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountingReportQuery(params);
    if (!query) {
        return undefined;
    }
    return await accountingReportsExportContractClient.exportExpenseByVendor({
        query: { ...query, sort, rowVisibility },
    });
}

export function useTrialBalanceQuery(query: AccountingReportFilters) {
    return useQuery({
        queryKey: getTrialBalanceQueryKey(query),
        queryFn: async () => {
            const preparedQuery = prepareAccountingReportQuery(query);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getTrialBalance({
                    query: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });
}

export async function exportTrialBalance(
    params: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountingReportQuery(params);
    if (!query) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportTrialBalance({
        query: { ...query, rowVisibility },
    });
}

export function useBalanceSheetQuery(
    query: AccountingReportFilters,
    options?: { enabled?: boolean },
) {
    const balanceSheet = useQuery({
        queryKey: getBalanceSheetQueryKey(query),
        enabled:
            options?.enabled !== false && !!prepareAccountingReportQuery(query),
        queryFn: async () => {
            const preparedQuery = prepareAccountingReportQuery(query);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getBalanceSheet({
                    query: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });

    const generationDate = useGenerationDate(balanceSheet.data);

    return { balanceSheet, generationDate };
}

export async function exportBalanceSheet(
    params: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountingReportQuery(params);
    if (!query) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportBalanceSheet({
        query: { ...query, rowVisibility },
    });
}

export async function exportProfitAndLoss(
    params: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountingReportQuery(params);
    if (!query) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportProfitAndLoss({
        query: { ...query, rowVisibility },
    });
}

export function useProfitAndLossQuery(
    query: AccountingReportFilters,
    options?: { enabled?: boolean; includeCounterpartyBreakdown?: boolean },
) {
    const includeCounterpartyBreakdown = options?.includeCounterpartyBreakdown;
    const profitAndLoss = useQuery({
        queryKey: [
            ...getProfitAndLossQueryKey(query),
            { includeCounterpartyBreakdown },
        ],
        enabled: options?.enabled && !!prepareAccountingReportQuery(query),
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const preparedQuery = prepareAccountingReportQuery(query);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getProfitAndLoss({
                    query: {
                        ...preparedQuery,
                        includeCounterpartyBreakdown,
                    },
                });
            return response.body;
        },
    });

    const generationDate = useGenerationDate(profitAndLoss.data);

    return { profitAndLoss, generationDate };
}

function getCashFlowStatementQueryKey(query: AccountingReportFilters) {
    return [accountingReportsQueryKeys.cashFlowStatement, query];
}

export function useCashFlowStatementQuery(query: AccountingReportFilters) {
    const cashFlowStatement = useQuery({
        queryKey: getCashFlowStatementQueryKey(query),
        queryFn: async () => {
            const preparedQuery = prepareAccountingReportQuery(query);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getCashFlowStatement({
                    query: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });

    const generationDate = useGenerationDate(cashFlowStatement.data);

    return { cashFlowStatement, generationDate };
}

export async function exportCashFlowStatement(
    params: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountingReportQuery(params);
    if (!query) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportCashFlowStatement({
        query: { ...query, rowVisibility },
    });
}

export interface AccountTransactionsQuery {
    filters: AccountingReportFilters;
    classifications?: AccountClass[];
    pnlBalanceSince?: Date | null;
    entityIds?: number[];
    format?: ReportFormat;
}

interface EliminationDetailReportQuery {
    workspaceId?: string;
    consolidationGroupId?: string;
    startDate?: Date;
    endDate?: Date;
    accountCode?: number;
}

interface EliminationExceptionsReportQuery {
    workspaceId?: string;
    consolidationGroupId?: string;
    startDate?: Date;
    endDate?: Date;
    entityId?: number;
    accountCode?: number;
    reason?: EliminationExceptionReason;
}

function prepareAccountTransactionsQuery(params: AccountTransactionsQuery) {
    const { filters, entityIds, pnlBalanceSince, ...restQuery } = params;
    const { startDate, endDate } = filters;

    if (!entityIds || entityIds.length === 0 || !filters.ledgerId) {
        return undefined;
    }

    return {
        ...restQuery,
        startDate: startDate
            ? formatISO(startDate, {
                  representation: "date",
              })
            : undefined,
        endDate: endDate
            ? formatISO(endDate, {
                  representation: "date",
              })
            : undefined,
        pnlBalanceSince: pnlBalanceSince
            ? formatISO(pnlBalanceSince, {
                  representation: "date",
              })
            : undefined,
        ...mapGeneralLedgerFilters(filters),
        entityIds,
        ledgerId: filters.ledgerId,
        transactionIds: filters.transactionIds,
        invoiceIds: filters.invoiceIds,
        shouldIncludeMatchedTransactions:
            filters.shouldIncludeMatchedTransactions,
        scheduleApplicationIds: filters.scheduleApplicationIds,
        scheduleJeSubtypes: filters.scheduleJeSubtypes,
        search: filters.search,
    };
}
async function getAccountTransactions(params: AccountTransactionsQuery) {
    const query = prepareAccountTransactionsQuery(params);
    if (!query) {
        return undefined;
    }

    const response =
        await accountingReportsContractClient.getAccountsTransactions({
            query,
        });
    return getAccountTransactionsResponseWithPresentationSchema.parse(
        response.body,
    );
}

export async function exportAccountTransactions(
    params: AccountTransactionsQuery,
    rowVisibility?: ReportSettingRowVisibility,
) {
    const query = prepareAccountTransactionsQuery(params);
    if (!query) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportAccountsTransactions(
        {
            query: { ...query, rowVisibility },
        },
    );
}

export function useAccountTransactionsQuery(
    params: AccountTransactionsQuery,
    options?: { enabled?: boolean },
) {
    const ledger = useQuery({
        queryKey: getAccountTransactionsQueryKey(params),
        queryFn: () => getAccountTransactions(params),
        enabled: options?.enabled,
        // Keep the prior report visible while the next entity/ledger selection
        // loads, instead of flashing an empty/loading state on every switch.
        placeholderData: keepPreviousData,
    });

    const generationDate = useGenerationDate(ledger.data);

    return { ledger, generationDate };
}

export function useEliminationDetailReportQuery(
    query: EliminationDetailReportQuery | undefined,
) {
    const hasRequiredParams = Boolean(
        query?.workspaceId &&
        query.consolidationGroupId &&
        query.startDate &&
        query.endDate,
    );

    return useQuery({
        queryKey: getEliminationDetailReportQueryKey(query),
        enabled: hasRequiredParams,
        queryFn: async () => {
            if (
                !query?.workspaceId ||
                !query.consolidationGroupId ||
                !query.startDate ||
                !query.endDate
            ) {
                return null;
            }

            const response =
                await accountingReportsContractClient.getEliminationDetailReport(
                    {
                        query: {
                            workspaceId: query.workspaceId,
                            consolidationGroupId: query.consolidationGroupId,
                            startDate: formatISO(query.startDate, {
                                representation: "date",
                            }),
                            endDate: formatISO(query.endDate, {
                                representation: "date",
                            }),
                            accountCode: query.accountCode,
                        },
                    },
                );

            return eliminationDetailReportResponseSchema.parse(response.body);
        },
        placeholderData: keepPreviousData,
    });
}

export function useEliminationExceptionsReportQuery(
    query: EliminationExceptionsReportQuery | undefined,
) {
    const hasRequiredParams = Boolean(
        query?.workspaceId &&
        query.consolidationGroupId &&
        query.startDate &&
        query.endDate,
    );

    return useQuery({
        queryKey: getEliminationExceptionsReportQueryKey(query),
        // Gate on the required params so the query function never runs — and so
        // never returns undefined, which React Query treats as an error — until
        // a consolidated context is available (e.g. standalone Balance Sheet or
        // before filters hydrate).
        enabled: hasRequiredParams,
        queryFn: async () => {
            if (
                !query?.workspaceId ||
                !query.consolidationGroupId ||
                !query.startDate ||
                !query.endDate
            ) {
                return null;
            }

            const response =
                await accountingReportsContractClient.getEliminationExceptions({
                    query: {
                        workspaceId: query.workspaceId,
                        consolidationGroupId: query.consolidationGroupId,
                        startDate: formatISO(query.startDate, {
                            representation: "date",
                        }),
                        endDate: formatISO(query.endDate, {
                            representation: "date",
                        }),
                        entityId: query.entityId,
                        accountCode: query.accountCode,
                        reason: query.reason,
                    },
                });

            return eliminationExceptionsReportResponseSchema.parse(
                response.body,
            );
        },
        placeholderData: keepPreviousData,
    });
}

export function useArAgingSummaryQuery(filters: AccountingReportFilters) {
    const arAgingSummary = useQuery({
        queryKey: getArAgingSummaryQueryKey(filters),
        queryFn: async () => {
            const preparedQuery = prepareArAgingReportQuery(filters);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getArAgingSummary({
                    body: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });

    const generationDate = useGenerationDate(arAgingSummary.data);

    return { arAgingSummary, generationDate };
}

export async function exportArAgingSummary(
    filters: AccountingReportFilters,
    format: ReportFormat,
) {
    const preparedQuery = prepareArAgingReportQuery(filters);
    if (!preparedQuery) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportArAgingSummary({
        body: { ...preparedQuery, format },
    });
}

export function useArAgingDetailQuery(
    filters: AccountingReportFilters,
    buckets: ArAgingBucket[] | undefined,
) {
    const arAgingDetail = useQuery({
        queryKey: getArAgingDetailQueryKey(filters, buckets),
        queryFn: async () => {
            const preparedQuery = prepareArAgingReportQuery(filters);
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getArAgingDetail({
                    body: { ...preparedQuery, buckets },
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
    });

    const generationDate = useGenerationDate(arAgingDetail.data);

    return { arAgingDetail, generationDate };
}

export async function exportArAgingDetail(
    filters: AccountingReportFilters,
    format: ReportFormat,
    buckets: ArAgingBucket[] | undefined,
) {
    const preparedQuery = prepareArAgingReportQuery(filters);
    if (!preparedQuery) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportArAgingDetail({
        body: { ...preparedQuery, format, buckets },
    });
}

export function useCheckDetailQuery(params: CheckDetailQueryParams) {
    const preparedQuery = prepareCheckDetailReportQuery(params);
    const checkDetail = useQuery({
        queryKey: [accountingReportsQueryKeys.checkDetail, preparedQuery],
        enabled: preparedQuery !== undefined,
        queryFn: async () => {
            if (!preparedQuery) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getCheckDetail({
                    body: preparedQuery,
                });
            return response.body;
        },
        placeholderData: keepPreviousData,
        retry: false,
    });

    const generationDate = useGenerationDate(checkDetail.data);

    return { checkDetail, generationDate };
}

interface ExportCheckDetailParams extends CheckDetailQueryParams {
    format: ReportFormat;
    groupBy: CheckDetailGroupBy;
    columns: CheckDetailColumn[];
}

export async function exportCheckDetail({
    format,
    groupBy,
    columns,
    ...params
}: ExportCheckDetailParams) {
    const preparedQuery = prepareCheckDetailReportQuery(params);
    if (!preparedQuery) {
        return undefined;
    }

    return await accountingReportsExportContractClient.exportCheckDetail({
        body: { ...preparedQuery, format, groupBy, columns },
    });
}

function useGenerationDate(report: any) {
    const [generationDate, setGenerationDate] = useState(() => new Date());
    useEffect(() => {
        if (report) {
            setGenerationDate(new Date());
        }
    }, [report, setGenerationDate]);

    return generationDate;
}

export async function exportTaxPackage(entityId: number, ledgerId: string) {
    const query = {
        entityId,
        startDate: "2025-01-01", // Hardcoding dates because we allow users to download only 2025 tax package for now
        endDate: "2025-12-31",
        ledgerId,
    };

    return await accountingReportsExportContractClient.exportTaxPackage({
        query,
    });
}

export async function exportBankReconciliation({
    bankReconciliationId,
    workspaceId,
}: {
    bankReconciliationId: string;
    workspaceId: string;
}) {
    return await accountingReportsExportContractClient.exportBankReconciliation(
        {
            params: { bankReconciliationId },
            query: {
                workspaceId,
                generatedDate: formatISO(new Date(), {
                    representation: "date",
                }),
            },
        },
    );
}

export function useBankReconciliationReportQuery({
    bankReconciliationId,
    enabled,
}: {
    bankReconciliationId: string | undefined;
    enabled: boolean;
}) {
    const workspace = useActiveWorkspace();
    const bankReconciliationReport = useQuery({
        queryKey: [
            accountingReportsQueryKeys.bankReconciliationReport,
            bankReconciliationId,
            workspace?.id,
        ],
        queryFn: async () => {
            if (!bankReconciliationId || !workspace) {
                return undefined;
            }
            const response =
                await accountingReportsContractClient.getBankReconciliation({
                    params: { bankReconciliationId },
                    query: { workspaceId: workspace.id },
                });
            return response.body;
        },
        enabled: enabled && !!bankReconciliationId && !!workspace,
        refetchOnWindowFocus: false,
    });

    const generationDate = useGenerationDate(bankReconciliationReport.data);

    return { bankReconciliationReport, generationDate };
}

export async function exportReportPackage(params: {
    entityIds: number[];
    reportTypes: AccountingReportType[];
    startDate: string;
    endDate: string;
    ledgerId: string;
    reportSettings?: Partial<
        Record<AccountingReportType, ReportPackageReportSetting>
    >;
}) {
    const { reportSettings, ...rest } = params;
    return await accountingReportsExportContractClient.exportReportPackage({
        query: {
            ...rest,
            reportSettings: reportSettings
                ? JSON.stringify(reportSettings)
                : undefined,
        },
    });
}

export async function exportReportPackageRows(
    body: z.infer<
        typeof accountingReportsContract.exportReportPackageRows.body
    >,
) {
    return await accountingReportsExportContractClient.exportReportPackageRows({
        body,
    });
}
